import { toRefs, watch, reactive, computed, ref } from "vue"
import { v4 as uuidv4 } from 'uuid'
import { Cart, getAvailablePaymentTypes } from "oido-data-structures"
import companyService from "./companyService"
import customerService from "./customerService"
import config from "../../config"
let pathParts = document.location.pathname.split('/')
import getCustomDomain from '../helpers/getCustomDomain'
import dayjs from "dayjs"

import slotsServiceModule from './slotsService'
const slotsService = slotsServiceModule()


import { useTimestamp } from '@vueuse/core'
const now = useTimestamp({ interval: 60000 })

import typedSystem from "./typedSystem"



let storageKeyExtension = ''

//on a custom domain we dont need no extension, because localstorage is bound to domain
if (!getCustomDomain()) {
    //old system
    if (pathParts.length > 2 && pathParts[1] == 'company') {
        storageKeyExtension = pathParts[2]
    }
    if (pathParts.length > 1 && pathParts[1] != 'company') {
        storageKeyExtension = pathParts[1]
    }
    if (!storageKeyExtension) {
        storageKeyExtension = uuidv4()
    }
    storageKeyExtension = '-' + storageKeyExtension
}

let localStorageKey = 'oido-store-v15' + storageKeyExtension

function writeToLocalStorage(data) {
    // console.log('writeToLocalStorage', data, JSON.stringify(data))
    localStorage.setItem(localStorageKey, JSON.stringify(data))
}
function readFromLocalStorage() {
    let data = JSON.parse(localStorage.getItem(localStorageKey))
    // console.log('readFromLocalStorage', data)
    return data
}

var stateTimer = null


const unsubscribeHandles = []

async function teardown() {
    for (let unsubscribeHandle of unsubscribeHandles) {
        await unsubscribeHandle()
    }
    //empty it
    unsubscribeHandles.length = 0
    clear()
}


const state = reactive({
    error: null,
    items: [],
    deliveryAddress: {},
    billingAddress: null,
    userSuppliesBillingAddress: false,
    deliveryUsesDifferentAddress: false,
    tipPercentage: 10,
    deliveryType: 'takeaway',
    //the delivery zone the user explicitly chose (null: the cheapest applicable one is used)
    deliveryZoneId: null,
    //the pickup location the user explicitly chose (null: the first one is used)
    pickupLocationId: null,
    pickupTimeMode: 'assoonaspossible',
    paymentType: 'online',
    pickupTime: null,
    phone: null,
    email: null,
    customerName: null,
    note: null,
    voucher: null,
    voucherDiscount: null,
    discounts: [],
    activeTags: [],
    errors: {},
    createCustomerIfNoneExists: false
})


// All add/match/remove logic lives in the shared Cart class (oido-data-structures).
// The Cart is bound to the SAME reactive array so Vue tracks its in-place mutations.
// Never reassign `state.items` (use cart.empty()/cart.items.push) or the binding breaks.
const cart = new Cart()
cart.items = state.items

function isEmptyish(value) {
    if (!value) return true
    if (Array.isArray(value)) return value.length === 0
    return Object.keys(value).length === 0
}

//The "plain" line for a product is the cart line with this productId and no
//options/slot picks. The menu (ProductListItem) stepper and quantity
//badge operate on this line; customized lines are addressed by their own id.
function findPlainLineForProduct(productId) {
    if (!productId) return null
    for (let item of state.items) {
        if (item && item.productId == productId && isEmptyish(item.options) && isEmptyish(item.chosen)) {
            return item
        }
    }
    return null
}

function clear() {
    cart.empty()
    state.tipPercentage = 10
    state.deliveryType = 'takeaway'
    state.pickupTimeMode = 'assoonaspossible'
    state.pickupTime = null
    state.paymentType = 'online'
    state.note = null
    state.voucher = null
    state.discounts = []
    state.phone = null
    state.email = null
    state.customerName = null
    state.deliveryAddress = state.deliveryAddress ? state.deliveryAddress : {}
    state.billingAddress = null
    state.deliveryUsesDifferentAddress = false
    state.userSuppliesBillingAddress = false
    state.createCustomerIfNoneExists = false
    initCartDefaults()
}

function emptyTheCart() {
    localStorage.removeItem(localStorageKey)
    if (stateTimer) {
        clearTimeout(stateTimer)
    }
    stateTimer = 'dont'
    clear()

    stateTimer = setTimeout(() => {
        localStorage.removeItem(localStorageKey)
        stateTimer = 'dont'
        clear()
        stateTimer = null
    }, 800)
}


//Accepts either a product (menu) or a cart line (cart views). A cart line is
//matched by its id; a product falls back to its plain line.
function changeNote(productOrItem, note) {
    if (!productOrItem || !productOrItem.id) {
        console.log('changeNote no product/item given', productOrItem)
        return
    }
    let line = cart.getItemById(productOrItem.id)
    if (!line) {
        line = findPlainLineForProduct(productOrItem.productId || productOrItem.id)
    }
    if (!line) {
        console.log('changeNote no matching cart line', productOrItem)
        return
    }
    line.note = note ? note : null
}

//Accepts either a product (menu badge → plain line) or a cart line (matched by id).
function getQuantityForProduct(productOrItem) {
    if (!productOrItem || !productOrItem.id) {
        console.warn('getQuantityForProduct no product/item given', productOrItem)
        return 0
    }
    let line = cart.getItemById(productOrItem.id)
    if (!line) {
        line = findPlainLineForProduct(productOrItem.productId || productOrItem.id)
    }
    return line && line.quantity ? Number.parseInt(line.quantity) : 0
}

//Aggregate quantity for a product across all cart entries — simple lines, variant
//lines (keyed `productId-||-uuid`), and combined-product slot picks. Used to enforce
//stock against the total order, not per cart line, so e.g. 3 separate lines of 1
//beer don't each pass an individual `quantity <= stock` check when stock < 3.
function getTotalQuantityForProductId(productId) {
    if (!productId) return 0
    let total = 0
    for (let item of state.items) {
        if (!item) continue
        let qty = Number.parseInt(item.quantity || 0)
        if (!qty) continue
        if (item.productId == productId) {
            total += qty
        }

        //For combined products with stockOfSubproductsAffectCombinedProduct on,
        //each slot pick also consumes a unit of the chosen subproduct's stock.
        if (item.chosen && item.productId) {
            let parentProduct = companyService.getProductById(item.productId)
            if (parentProduct && parentProduct.type == 'combined' && parentProduct.stockOfSubproductsAffectCombinedProduct) {
                for (let slotId in item.chosen) {
                    if (item.chosen[slotId] == productId) {
                        total += qty
                    }
                }
            }
        }
    }
    return total
}

//Adds a configured product to the cart. The shared Cart merges it into an
//existing line with the same product/options/slots/note (bumping the
//quantity) or pushes a new line. courseId is left null; the order calculator
//falls back to each product's defaultCourseId for kitchen/receipt grouping.
function addProductToBasket(product, chosenItems, options, quantity) {

    if (!quantity) {
        quantity = 1
    }
    if (!product || !product.id) {
        console.log('addProductToBasket no product given', product)
        return 0
    }

    cart.saveItem({
        id: null,
        productId: product.id,
        quantity: quantity,
        note: null,
        courseId: null,
        chosen: chosenItems && Object.keys(chosenItems).length > 0 ? chosenItems : {},
        options: options && Object.keys(options).length > 0 ? options : null,
    })
}

function deleteItemFromCart(item) {
    if (!item || !item.id) {
        console.log('deleteItemFromCart no item given', item)
        return
    }
    cart.deleteItem(item)
}

//Decrements a cart line. A cart line is matched by id; a menu stepper passes a
//product-shaped object and resolves to the product's plain line. Removes the
//line when it hits zero.
function countCartItemDown(item) {
    if (!item || !item.id) {
        console.log('countCartItemDown no item given', item)
        return
    }
    let line = cart.getItemById(item.id)
    if (!line) {
        line = findPlainLineForProduct(item.productId || item.id)
    }
    if (!line) {
        return
    }
    line.quantity = line.quantity && line.quantity > 1 ? line.quantity - 1 : 0
    if (line.quantity <= 0) {
        cart.deleteItem(line)
    }
}

//Increments a cart line. Matches an existing line by id (cart views) or the
//product's plain line (menu stepper); otherwise adds a fresh plain line.
function countCartItemUp(item) {
    if (!item || !item.id) {
        console.log('countCartItemUp no item given', item)
        return 0
    }
    let line = cart.getItemById(item.id)
    if (!line) {
        line = findPlainLineForProduct(item.productId || item.id)
    }
    if (line) {
        line.quantity = line.quantity ? line.quantity + 1 : 1
        return
    }
    cart.saveItem({
        id: null,
        productId: item.productId || item.id,
        quantity: 1,
        note: null,
        courseId: null,
        chosen: {},
        options: null,
    })
}

/**
 * Repopulates the cart from a previously placed order so the customer can re-order it.
 * Returns { added: <number of line items pushed>, skipped: <number of items whose product no longer exists> }.
 * Items whose product is no longer available are silently skipped.
 */
function repeatOrder(order) {
    let added = 0
    let skipped = 0

    if (!order || !Array.isArray(order.orderItems)) {
        return { added, skipped }
    }

    for (let orderItem of order.orderItems) {
        let product = companyService.getProductById(orderItem.productId)
        if (!product) {
            skipped++
            continue
        }

        let chosenItems = {}
        if (orderItem.chosen && typeof orderItem.chosen == 'object') {
            for (let slotId in orderItem.chosen) {
                let slot = orderItem.chosen[slotId]
                if (slot && slot.chosen && slot.chosen.productId) {
                    chosenItems[slotId] = slot.chosen.productId
                }
            }
        }

        let options = {}
        if (Array.isArray(orderItem.options)) {
            for (let option of orderItem.options) {
                if (!option || !option.id || !Array.isArray(option.values)) continue
                let valueIds = option.values.map((v) => v && v.id).filter(Boolean)
                if (valueIds.length === 1) {
                    options[option.id] = valueIds[0]
                } else if (valueIds.length > 1) {
                    options[option.id] = valueIds
                }
            }
        }

        let quantity = Number.parseInt(orderItem.quantity) || 1
        addProductToBasket(product, chosenItems, options, quantity)
        added = quantity
    }

    return { added, skipped }
}


function totalNumberOfItems() {
    let total = 0
    for (let item of state.items) {
        if (item && item.quantity) {
            total = total + item.quantity
        }
    }
    return total
}


function productsInCart() {
    let products = []
    for (let item of state.items) {
        let product = companyService.getProductById(item.productId)
        if (product) {
            products.push(product)
        }
    }
    return products
}

//Returns shallow copies of the cart lines with their resolved `product` attached
//for display. Copies (not the live lines) so the transient `product` field never
//leaks into persistence/submission; mutations still go through the service
//methods (matched by id) which operate on the live lines.
function itemsInCart() {
    let itemsWithProduct = []
    for (let item of state.items) {
        let copy = { ...item }
        let product = companyService.getProductById(item.productId)
        if (product) {
            copy.product = product
        }
        itemsWithProduct.push(copy)
    }
    return itemsWithProduct
}


//Company/point-of-sale dependent cart defaults (tip, payment type, delivery
//type). Formerly part of initItemsByCourse; the course construct is gone.
function initCartDefaults() {
    if (!companyService.company || !companyService.company.value || !companyService.company.value.id) {
        return
    }

    let company = companyService.company.value
    state.tipPercentage = company.defaultTipPercentage

    //the delivery type has a say in the payment options (deliveries are not paid on
    //site), so settle it first and let the payment type follow
    resetDeliveryTypeIfUnavailable()

    //the options are returned in preference order (online, on site, on invoice), so the
    //first one is the default. there should always be at least one of them enabled
    let availablePaymentTypes = getAvailablePaymentTypesForCart()
    state.paymentType = availablePaymentTypes.length > 0 ? availablePaymentTypes[0] : "online"
}

//the ways to pay the customer may choose from right now: what the point of sale offers,
//narrowed down by the company wide cash sales consent, the chosen delivery type and the
//logged in customer's own overrides
function getAvailablePaymentTypesForCart() {
    let company = companyService.company && companyService.company.value ? companyService.company.value : null
    let activePointofsale = companyService.activePointofsale && companyService.activePointofsale.value ? companyService.activePointofsale.value : null
    let customer = customerService.customer && customerService.customer.value ? customerService.customer.value : null
    return getAvailablePaymentTypes(activePointofsale, company, state.deliveryType, customer)
}

const availablePaymentTypes = computed(() => getAvailablePaymentTypesForCart())

//a payment type can become unavailable while the cart lives on: the customer switches to
//a delivery, or the point of sale changes its options
function resetPaymentTypeIfUnavailable() {
    let paymentTypes = getAvailablePaymentTypesForCart()
    if (paymentTypes.length > 0 && !paymentTypes.includes(state.paymentType)) {
        state.paymentType = paymentTypes[0]
    }
}

//the available options depend on the active point of sale, and a logged in customer can
//override its delivery options, so this has to run again whenever the customer changes
//and not only when the company is loaded
function resetDeliveryTypeIfUnavailable() {
    let activePointofsale = companyService.activePointofsale && companyService.activePointofsale.value ? companyService.activePointofsale.value : null
    if (!activePointofsale) {
        return
    }

    let availableDeliveryOptions = []
    if (activePointofsale.enableTableService) {
        availableDeliveryOptions.push('table')
    }
    if (activePointofsale.enablePickup) {
        availableDeliveryOptions.push('takeaway')
    }
    if (activePointofsale.enableDelivery) {
        availableDeliveryOptions.push('delivery')
    }
    if (availableDeliveryOptions.length > 0 && !availableDeliveryOptions.includes(state.deliveryType)) {
        state.deliveryType = availableDeliveryOptions[0]
    }
}

//Drop cart lines whose product (or, for combined products, a chosen subproduct)
//no longer exists. Replaces the old itemsByCourse-based validation.
function validateCartItems() {
    if (!companyService.company || !companyService.company.value || !companyService.company.value.id) {
        return
    }

    let toRemove = []
    for (let item of state.items) {
        if (!item) continue
        let product = companyService.getProductById(item.productId)
        if (!product) {
            toRemove.push(item)
            continue
        }
        if (product.type == 'combined' && item.chosen) {
            for (let slotId in item.chosen) {
                if (!companyService.getProductById(item.chosen[slotId])) {
                    toRemove.push(item)
                    break
                }
            }
        }
    }
    for (let item of toRemove) {
        cart.deleteItem(item)
    }
}

function getCartItemById(cartItemId) {
    return cart.getItemById(cartItemId)
}

function validateCart() {
    state.errors = {}
    if (state.deliveryType == 'takeaway' && state.pickupTimeMode == 'custom') {
        if (!state.pickupTime) {
            state.errors.pickupTime = ['required']
        }
        if (state.pickupTime && dayjs(state.pickupTime).isBefore(dayjs(firstAllowedPickupOrDeliveryDate.value))) {
            state.errors.pickupTime = ['required']
        }
    }
}


function percentageOfAllowedOrderForSlot(numberOfOrders) {
    let company = companyService.company.value
    if (!company || !company.enableSlotLimits) {
        return 0
    }
    if (!numberOfOrders) {
        return 0
    }
    if (!company.allowedNumberOfOrdersPerSlot) {
        return 0
    }

    let percentage = numberOfOrders / company.allowedNumberOfOrdersPerSlot * 100

    return percentage
}

//the delivery zone applicable to the current cart (null when it's not a delivery or no zone matches)
function currentDeliveryZone() {
    if (state.deliveryType != 'delivery') {
        return null
    }
    let calculator = typedSystem.orderCalculator && typedSystem.orderCalculator.value ? typedSystem.orderCalculator.value : null
    let applicable = calculator ? calculator.getApplicableDeliveryZone() : null
    return applicable && applicable.zone ? applicable.zone : null
}

//the date picker rebuilds the slot lists of 100 days on every render — without a
//cache each cart interaction froze the UI for a second. the cache holds one slot
//list per day and is thrown away whenever an input that changes the slots does.
//slotAllocationVersion is a reactive ref so occupancy updates still re-render.
let slotsForDayCache = { fingerprint: null, byDay: new Map() }
const slotAllocationVersion = ref(0)
watch(() => slotsService.slots && slotsService.slots.value ? slotsService.slots.value : null, () => {
    slotAllocationVersion.value++
}, { deep: true })

function getSlotsForDay(date, minDate = undefined) {
    if (!date) {
        return []
    }
    let calculator = typedSystem.orderCalculator && typedSystem.orderCalculator.value ? typedSystem.orderCalculator.value : null
    if (!calculator) {
        return []
    }

    let isDelivery = state.deliveryType == 'delivery'
    //delivery slots are limited to the zone's delivery time table — a zone without
    //a limitation offers the whole day. pickup slots are limited to the chosen pickup
    //location's time table (the opening hours when the location doesn't limit them,
    //the whole day when there is no pickup location at all — same fallbacks as
    //OrderCalculator#getProductionSchedule).
    let timeTableId = 'openingHours'
    if (isDelivery) {
        let deliveryZone = currentDeliveryZone()
        timeTableId = deliveryZone && deliveryZone.deliveryTimeTableId ? deliveryZone.deliveryTimeTableId : '*unrestricted*'
    }
    if (state.deliveryType == 'takeaway') {
        let pickupLocation = calculator.getChosenPickupLocation()
        if (!pickupLocation) {
            timeTableId = '*unrestricted*'
        } else if (pickupLocation.pickupTimeTableId) {
            timeTableId = pickupLocation.pickupTimeTableId
        }
    }

    //no minDate given: constrain the slots to the first allowed pickup/delivery date.
    //callers inside that very computed pass an explicit minDate (null) so the computed doesn't read itself
    if (minDate === undefined) {
        minDate = firstAllowedPickupOrDeliveryDate.value
    }

    let fingerprint = [
        state.deliveryType,
        timeTableId,
        minDate ? dayjs(minDate).valueOf() : null,
        slotAllocationVersion.value,
        Math.floor(now.value / 60000),
    ].join(':')

    if (slotsForDayCache.fingerprint != fingerprint) {
        slotsForDayCache = { fingerprint, byDay: new Map() }
    }
    let dayKey = dayjs(date).valueOf()
    if (slotsForDayCache.byDay.has(dayKey)) {
        return slotsForDayCache.byDay.get(dayKey)
    }

    let slots = []
    let slotWidth = 15
    let nowDate = dayjs(now.value)
    let openingHours = timeTableId == '*unrestricted*'
        ? calculator.getUnrestrictedHoursForDay(date)
        : calculator.getTimeTableHoursForDay(date, timeTableId)

    if (openingHours) {
        for (let range of openingHours) {
            let slotStartTime = dayjs(range.startDate)

            while (slotStartTime.add(slotWidth, 'minutes').isSameOrBefore(range.endDate)) {
                //only push slots that are after the minumum startdate

                let numberOfOrders = slotsService.getNumberOfOrdersForSlot(slotStartTime)
                let percentage = percentageOfAllowedOrderForSlot(numberOfOrders)
                slots.push({
                    start: slotStartTime,
                    end: slotStartTime.add(slotWidth, 'minutes'),
                    openingHours: range,
                    isInThePast: slotStartTime.isBefore(nowDate),
                    isSelectable: (!minDate || slotStartTime.isSameOrAfter(minDate, 'minute')) && (percentage < 100),
                    numberOfOrders: numberOfOrders,
                    allocationPercentage: percentage
                })
                slotStartTime = slotStartTime.add(slotWidth, 'minutes')
            }
        }
    }

    slotsForDayCache.byDay.set(dayKey, slots)
    return slots
}



/**
 * the shared production/delivery timing calculation lives in the
 * OrderCalculator (oido-data-structures) so the server runs the exact
 * same logic when an order comes in — this just invokes it for the cart
 */
function currentProductionSchedule() {
    let calculator = typedSystem.orderCalculator && typedSystem.orderCalculator.value ? typedSystem.orderCalculator.value : null
    if (!calculator) {
        return null
    }
    if (!state.items || state.items.length == 0) {
        return null
    }
    return calculator.getProductionSchedule(new Date(now.value))
}


/**
 * moves the date onto the first available timeslot on or after it, skipping full slots
 * (getSlotsForDay builds the slots from the chosen pickup location's hours for pickups
 * and from the delivery hours for deliveries)
 */
function firstDateWithinAvailableTimeslots(earliestDate) {
    if (!earliestDate) {
        return null
    }
    earliestDate = dayjs(earliestDate)

    let numberOfDaysTried = 0
    let maxDaysToTry = 75
    let loopedDate = dayjs(earliestDate)

    while (numberOfDaysTried < maxDaysToTry) {
        for (let slot of getSlotsForDay(loopedDate, null)) {

            let slotIsFull = slot.allocationPercentage >= 100

            //the date is within this timeslot: keep it
            if (earliestDate.isSameOrAfter(slot.start) && earliestDate.isBefore(slot.end)) {
                if (!slotIsFull) {
                    return earliestDate
                }
                continue
            }

            //otherwise take the start of the first timeslot after the date that still has capacity
            if (earliestDate.isSameOrBefore(slot.start) && !slotIsFull) {
                return slot.start
            }
        }
        numberOfDaysTried++
        loopedDate = loopedDate.add(1, 'day')
    }

    return null
}

const allowedToAddBillingAddress = computed(() => {
    let customer = customerService.customer && customerService.customer.value ? customerService.customer.value : null
    return !customer || !customer.userMustNotEditAddresses

})
const needsBillingAddress = computed(() => {
    let company = companyService.company && companyService.company.value
    if (!company) {
        return false
    }

    let simplifiedInvoiceTreshold = company.simplifiedInvoiceTreshold ? Number.parseFloat(company.simplifiedInvoiceTreshold) : 0.0
    if (cartBreakdown.value) {
        let total = cartBreakdown.value.total
        if (total > simplifiedInvoiceTreshold) {
            return true
        }
    }

    let activePointofsale = companyService.activePointofsale && companyService.activePointofsale.value ? companyService.activePointofsale.value : null
    if (!activePointofsale) {
        return false
    }
    return Boolean(activePointofsale.collectBillingAddress)
})


const cartBreakdown = computed(() => {
    let company = companyService.company && companyService.company.value
    if (!company) {
        return null
    }
    if (typedSystem.orderCalculator && typedSystem.orderCalculator.value) {
        return typedSystem.orderCalculator.value.getCartBreakDown()
    }
    return null

})


/**
 * If the customer is just short of a cheaper bracket in the currently-applicable delivery zone,
 * suggest how much more to add. Prefers a free bracket if any is reachable by adding net.
 * Returns { addAmount, savings, free } or null.
 */
const deliveryUpgrade = computed(() => {
    if (state.deliveryType != 'delivery') return null
    let calc = typedSystem.orderCalculator && typedSystem.orderCalculator.value
    if (!calc) return null

    let applicable = calc.getApplicableDeliveryZone()
    if (!applicable || !applicable.zone || !applicable.zone.prices) return null

    let currentDeliveryCost = applicable.price
    if (currentDeliveryCost == null || currentDeliveryCost <= 0) return null

    let currentSubtotal = calc.getCombinedSubtotal()
    let currentWeight = calc.getCombinedWeight()

    let best = null
    for (let bracket of applicable.zone.prices) {
        if (bracket.price == null) continue
        if (bracket.minSubtotal == null) continue
        let bracketPrice = Number.parseFloat('' + bracket.price)
        let bracketMin = Number.parseFloat('' + bracket.minSubtotal)
        if (isNaN(bracketPrice) || isNaN(bracketMin)) continue
        if (bracketMin <= currentSubtotal) continue
        if (bracketPrice >= currentDeliveryCost) continue
        let maxWeight = bracket.maxWeight ? Number.parseFloat('' + bracket.maxWeight) : Number.MAX_SAFE_INTEGER
        if (maxWeight < currentWeight) continue

        let candidate = {
            addAmount: bracketMin - currentSubtotal,
            savings: currentDeliveryCost - bracketPrice,
            free: bracketPrice == 0
        }
        // Prefer a free upgrade; otherwise prefer the one with the biggest savings.
        if (!best) {
            best = candidate
            continue
        }
        if (candidate.free && !best.free) {
            best = candidate
            continue
        }
        if (best.free && !candidate.free) continue
        if (candidate.savings > best.savings) best = candidate
    }
    return best
})


const needsDeliveryAddress = computed(() => {

    if (state.deliveryType != 'delivery') {
        return false
    }

    //if its delivery but has no billingaddresse (because the zone doesn't require it and the user didn't add one voluntarily )
    // od the user user wants to add a delivery addresse which differs from the billing address
    if ((!needsBillingAddress.value && !state.userSuppliesBillingAddress) || state.deliveryUsesDifferentAddress) {
        console.log('needsBillingAddress herewego', needsBillingAddress, state.deliveryUsesDifferentAddress)
        return true
    }

    return false
})


/** 
 * returns the first possible date for pickup or delivery (depending on the state)
 *
 * 1. production: every item's production time is fitted into the production hours,
 *    the order is ready once the production of its last item is finished
 * 2. delivery only: the expected delivery time of the applicable zone is added,
 *    fitted into the zone's delivery time table when one is selected
 * 3. the resulting date is moved onto the first available timeslot:
 *    within the pickup location's time table for pickups, within the zone's delivery time table for deliveries
 */
//the fitting scans below are expensive: they walk up to 75 days of time table
//windows and slot allocations. the calculator depends on every cart field, so
//without this cache each keystroke in the checkout re-ran the whole scan and
//froze the UI. only the fingerprinted inputs can change the result — the
//minute tick refreshes the cache so "now" stays fresh.
let firstAllowedDateCache = { fingerprint: null, result: null }

const firstAllowedPickupOrDeliveryDate = computed(() => {

    let company = companyService.company && companyService.company.value ? companyService.company.value : null
    let activePointofsale = companyService.activePointofsale && companyService.activePointofsale.value ? companyService.activePointofsale.value : null

    let fingerprint = JSON.stringify([
        state.items,
        state.deliveryType,
        state.deliveryZoneId,
        state.pickupLocationId,
        state.deliveryAddress,
        state.billingAddress,
        state.deliveryUsesDifferentAddress,
        state.userSuppliesBillingAddress,
        company ? company.translationVersion : null,
        activePointofsale ? activePointofsale.id : null,
        slotsService.slots && slotsService.slots.value ? slotsService.slots.value : null,
        Math.floor(now.value / 60000),
    ])

    if (firstAllowedDateCache.fingerprint == fingerprint) {
        return firstAllowedDateCache.result
    }

    let result = null
    let schedule = currentProductionSchedule()
    if (schedule && schedule.productionEndDate) {
        let readyDate = state.deliveryType == 'delivery' ? schedule.firstPossibleDeliveryDate : schedule.productionEndDate
        if (readyDate) {
            result = firstDateWithinAvailableTimeslots(dayjs(readyDate))
        }
    }

    firstAllowedDateCache = { fingerprint, result }
    return result
})


    let savedData = readFromLocalStorage()
    if (savedData) {
        for (let key in savedData) {
            //items are restored in place (push) so the Cart binding to the
            //reactive array is preserved; never reassign state.items.
            if (key == 'items' || key == 'itemsByCourse') continue
            state[key] = savedData[key]
        }
        if (Array.isArray(savedData.items)) {
            cart.items.push(...savedData.items)
        }
    }
    initCartDefaults()
    validateCartItems()



// set billing address to an empty object as soon its needed or null if not
watch(needsBillingAddress, (needsBillingAddress) => {
    console.log('needsBillingAddress', needsBillingAddress, state.userSuppliesBillingAddress)
    state.billingAddress = needsBillingAddress || state.userSuppliesBillingAddress ? (state.billingAddress ? state.billingAddress : {}) : null
})

//a chosen pickup/delivery date is only valid for the selections it was calculated
//from: when any of them changes (delivery type, pickup location, delivery method,
//the address the delivery method depends on, or the cart's contents) the date is
//cleared so the user has to choose again.
//registered AFTER the localStorage restore above so a page reload keeps the date.
watch(
    () => {
        //the address only influences the date for deliveries (it determines the
        //applicable zone) — same fallback the order calculator uses
        let addressInUse = null
        if (state.deliveryType == 'delivery') {
            let deliveryAddressIsSet = state.deliveryAddress && Object.keys(state.deliveryAddress).length > 0
            addressInUse = deliveryAddressIsSet ? state.deliveryAddress : state.billingAddress
        }
        return JSON.stringify([
            state.items,
            state.deliveryType,
            state.deliveryZoneId,
            state.pickupLocationId,
            addressInUse,
        ])
    },
    (newFingerprint, oldFingerprint) => {
        if (newFingerprint == oldFingerprint) {
            return
        }
        if (state.pickupTime) {
            state.pickupTime = null
        }
    }
)

//set delivery address to an empty object as soon its needed and its empty
// don't set it to null because if the user enters a deliver address and later on decides that he needs a billing address too, it would delete the delivery address
watch(needsDeliveryAddress, (needsDeliveryAddress) => {
    if (needsDeliveryAddress && !state.deliveryAddress) {
        state.deliveryAddress = {}
    }
})


function copyCustomersAddressesIfNeeded() {
    let customer = customerService.customer && customerService.customer.value ? customerService.customer.value : null
    console.log("customer", customer)
    if (customer) {

        if (customer.name) {
            state.customerName = customer.name
        }
        if (customer.phone) {
            state.phone = customer.phone
        }
        if (customer.email) {
            state.email = customer.email
        }

        if (customer.billingAddress) {
            state.billingAddress = customer.billingAddress
            state.userSuppliesBillingAddress = true
        }

        if (customer.deliveryAddress) {
            state.deliveryAddress = customer.deliveryAddress
            state.deliveryUsesDifferentAddress = true
        } else {
            if (customer.billingAddress) {
                state.deliveryAddress = JSON.parse(JSON.stringify(customer.billingAddress))
                state.userSuppliesBillingAddress = true
            }
        }
    } else {
        state.billingAddress = {}
        state.userSuppliesBillingAddress = false
        state.deliveryAddress = {}
        state.deliveryUsesDifferentAddress = false
        state.customerName = null
        state.phone = null
        state.email = null
    }
}



watch(customerService.customer, (customer, oldValue) => {
    copyCustomersAddressesIfNeeded()
    //the customer may bring its own delivery and payment options: drop a selection they
    //don't allow. the delivery type goes first, it has a say in the payment options
    resetDeliveryTypeIfUnavailable()
    resetPaymentTypeIfUnavailable()
})

//deliveries are not paid on site, so switching to (or away from) a delivery can invalidate
//the chosen way to pay
watch(() => state.deliveryType, () => {
    resetPaymentTypeIfUnavailable()
})


watch(companyService.company, (company) => {

    if (!company) {
        console.log('no company!!!!')
        clear()
        window.location.replace(config.url)
        return
    }

    let existingTagIds = company.tags && company.tags.map((tag) => tag.id)
    let cleanedActiveTags = []
    for (let activeTagId of state.activeTags) {
        if (existingTagIds && existingTagIds.includes(activeTagId)) {
            cleanedActiveTags.push(activeTagId)
        }
    }
    state.activeTags = cleanedActiveTags

    initCartDefaults()
    validateCartItems()
})

watch(state, async () => {
    if (!stateTimer) {
        stateTimer = setTimeout(() => {
            writeToLocalStorage({
                items: state.items,
                deliveryAddress: state.deliveryAddress,
                billingAddress: state.billingAddress,
                deliveryUsesDifferentAddress: state.deliveryUsesDifferentAddress,
                userSuppliesBillingAddress: state.userSuppliesBillingAddress,
                createCustomerIfNoneExists: state.createCustomerIfNoneExists,
                tipPercentage: state.tipPercentage,
                deliveryType: state.deliveryType,
                deliveryZoneId: state.deliveryZoneId,
                pickupLocationId: state.pickupLocationId,
                pickupTimeMode: state.pickupTimeMode,
                pickupTime: state.pickupTime,
                note: state.note,
                // voucher: state.voucher,
                phone: state.phone,
                email: state.email,
                customerName: state.customerName
            })

            stateTimer = null
        }, 500)
    }
}, { deep: true })



export default {
    ...toRefs(state),
    changeNote,
    getQuantityForProduct,
    getTotalQuantityForProductId,
    totalNumberOfItems,
    productsInCart,
    itemsInCart,
    clear,
    emptyTheCart,
    deleteItemFromCart,
    addProductToBasket,
    repeatOrder,
    getCartItemById,
    countCartItemUp,
    countCartItemDown,
    localStorageKey,
    validateCart,
    getSlotsForDay,
    firstAllowedPickupOrDeliveryDate,
    needsBillingAddress,
    needsDeliveryAddress,
    allowedToAddBillingAddress,
    deliveryUpgrade,
    availablePaymentTypes
}


